import imageCompression from 'browser-image-compression';

export const REVIEW_IMAGE_MAX_SIZE = 1 * 1024 * 1024;
export const REVIEW_VIDEO_MAX_SIZE = 5 * 1024 * 1024;
export const REVIEW_IMAGE_SOURCE_MAX_SIZE = 20 * 1024 * 1024;
export const REVIEW_VIDEO_SOURCE_MAX_SIZE = 50 * 1024 * 1024;

const IMAGE_MAX_DIMENSION = 1600;
const VIDEO_MAX_DIMENSION = 1280;
const FFMPEG_CORE_BASE_URL =
  'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd';

let ffmpegInstance;

// 브라우저에서 영상 정보를 읽어 변환 여부와 목표 비트레이트를 계산합니다.
const getVideoMetadata = (file) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      });
      URL.revokeObjectURL(objectUrl);
    };
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('동영상 정보를 확인할 수 없습니다.'));
    };
    video.src = objectUrl;
  });

// FFmpeg WASM은 영상이 선택된 경우에만 내려받아 초기 로딩 용량을 줄입니다.
const getFFmpeg = async () => {
  if (ffmpegInstance?.loaded) return ffmpegInstance;

  const [{ FFmpeg }, { toBlobURL }] = await Promise.all([
    import('@ffmpeg/ffmpeg'),
    import('@ffmpeg/util'),
  ]);

  ffmpegInstance = ffmpegInstance || new FFmpeg();
  await ffmpegInstance.load({
    coreURL: await toBlobURL(
      `${FFMPEG_CORE_BASE_URL}/ffmpeg-core.js`,
      'text/javascript',
    ),
    wasmURL: await toBlobURL(
      `${FFMPEG_CORE_BASE_URL}/ffmpeg-core.wasm`,
      'application/wasm',
    ),
  });

  return ffmpegInstance;
};

// 이미지 크기와 용량을 브라우저에서 줄여 변환된 File을 반환합니다.
const processImage = async (file, onProgress) => {
  if (file.type === 'image/gif') {
    if (file.size > REVIEW_IMAGE_MAX_SIZE) {
      throw new Error('움직이는 GIF 이미지는 1MB 이하만 등록 가능합니다.');
    }
    return file;
  }

  const compressedFile = await imageCompression(file, {
    maxSizeMB: REVIEW_IMAGE_MAX_SIZE / (1024 * 1024),
    maxWidthOrHeight: IMAGE_MAX_DIMENSION,
    useWebWorker: true,
    preserveExif: false,
    initialQuality: 0.85,
    onProgress,
  });

  if (compressedFile.size > REVIEW_IMAGE_MAX_SIZE) {
    throw new Error('이미지를 1MB 이하로 줄일 수 없습니다.');
  }

  // 압축 모듈이 반환한 Blob을 FileManager가 허용하는 실제 File로 변환합니다.
  return new File([compressedFile], file.name, {
    type: compressedFile.type || file.type,
    lastModified: file.lastModified,
  });
};

// 영상 해상도와 비트레이트를 조정해 MP4 파일로 변환합니다.
const processVideo = async (file, onProgress) => {
  const metadata = await getVideoMetadata(file);
  const needsProcessing =
    file.size > REVIEW_VIDEO_MAX_SIZE ||
    Math.max(metadata.width, metadata.height) > VIDEO_MAX_DIMENSION;

  if (!needsProcessing) return file;

  const ffmpeg = await getFFmpeg();
  const inputExtension = file.name.split('.').pop()?.toLowerCase() || 'video';
  const uniqueId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const inputName = `review-input-${uniqueId}.${inputExtension}`;
  const outputName = `review-output-${uniqueId}.mp4`;
  const outputFileName = file.name.replace(/\.[^.]+$/, '') + '.mp4';
  const duration = Math.max(metadata.duration || 1, 1);
  const totalBitrate = Math.floor(
    ((REVIEW_VIDEO_MAX_SIZE * 0.9 * 8) / duration) * 0.001,
  );
  const videoBitrate = Math.max(250, Math.min(totalBitrate - 96, 2500));
  const progressHandler = ({ progress }) => {
    onProgress(Math.min(100, Math.max(0, Math.round(progress * 100))));
  };

  ffmpeg.on('progress', progressHandler);

  try {
    const { fetchFile } = await import('@ffmpeg/util');
    await ffmpeg.writeFile(inputName, await fetchFile(file));

    const exitCode = await ffmpeg.exec([
      '-i',
      inputName,
      '-vf',
      `scale=${VIDEO_MAX_DIMENSION}:${VIDEO_MAX_DIMENSION}:force_original_aspect_ratio=decrease:force_divisible_by=2`,
      '-c:v',
      'libx264',
      '-preset',
      'veryfast',
      '-b:v',
      `${videoBitrate}k`,
      '-maxrate',
      `${videoBitrate}k`,
      '-bufsize',
      `${videoBitrate * 2}k`,
      '-c:a',
      'aac',
      '-b:a',
      '96k',
      '-movflags',
      '+faststart',
      outputName,
    ]);

    if (exitCode !== 0) {
      throw new Error('동영상 변환에 실패했습니다.');
    }

    const outputData = await ffmpeg.readFile(outputName);
    const processedFile = new File([outputData.buffer], outputFileName, {
      type: 'video/mp4',
      lastModified: Date.now(),
    });

    if (processedFile.size > REVIEW_VIDEO_MAX_SIZE) {
      throw new Error('업로드를 실패했습니다.');
    }

    return processedFile;
  } finally {
    ffmpeg.off('progress', progressHandler);
    await Promise.allSettled([
      ffmpeg.deleteFile(inputName),
      ffmpeg.deleteFile(outputName),
    ]);
  }
};

// 선택된 리뷰 파일을 한 개씩 처리해 브라우저 메모리 사용량을 제한합니다.
export const processReviewMediaFiles = async (files, onStatus) => {
  const processedFiles = [];

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    const current = index + 1;

    if (file.type.startsWith('image/')) {
      if (file.size > REVIEW_IMAGE_SOURCE_MAX_SIZE) {
        throw new Error('이미지는 20MB 이하만 선택할 수 있습니다.');
      }

      const processedFile = await processImage(file, (progress) => {
        onStatus(`이미지 ${current}/${files.length} 처리 중 (${progress}%)`);
      });
      processedFiles.push(processedFile);
      continue;
    }

    if (file.type.startsWith('video/')) {
      if (file.size > REVIEW_VIDEO_SOURCE_MAX_SIZE) {
        throw new Error('동영상은 50MB 이하만 선택할 수 있습니다.');
      }

      onStatus(`동영상 ${current}/${files.length} 준비 중`);
      const processedFile = await processVideo(file, (progress) => {
        onStatus(`동영상 ${current}/${files.length} 처리 중 (${progress}%)`);
      });
      processedFiles.push(processedFile);
      continue;
    }

    throw new Error('이미지 또는 동영상 파일만 등록할 수 있습니다.');
  }

  return processedFiles;
};
